/**
 * Who is paid for which time.
 *
 * "Billable" only means billed to a client. Whether a worker is paid for an
 * hour follows their pay basis (`ContractorPayBasisEnum`):
 *  - Employees (full- and part-time) are paid for every hour.
 *  - Contractors and freelancers follow their own basis, else the org default:
 *    ALL_HOURS pays every hour (non-billable time is internal cost);
 *    BILLABLE_HOURS pays billable hours only (non-billable time is unpaid);
 *    RETAINER pays a fixed amount per pay period, not per hour.
 *
 * Stored per-entry pay (`totalPayAmount`) is the pay *value* of the hours at
 * the pay rate; every place that sums pay must apply these helpers.
 */
import { isContractorEmploymentType } from '../types/member-resource'

export type ContractorPayBasis = 'ALL_HOURS' | 'BILLABLE_HOURS' | 'RETAINER'

export const DEFAULT_CONTRACTOR_PAY_BASIS: ContractorPayBasis = 'BILLABLE_HOURS'

function isPayBasis(value: unknown): value is ContractorPayBasis {
  return (
    value === 'ALL_HOURS' || value === 'BILLABLE_HOURS' || value === 'RETAINER'
  )
}

/**
 * The pay basis that applies to a member: employees are always ALL_HOURS;
 * contractors use their own basis, else the org default. A RETAINER without an
 * amount can't be paid, so it falls back to the org default.
 */
export function resolveContractorPayBasis({
  employmentType,
  memberPayBasis,
  retainerAmount,
  orgDefault,
}: {
  employmentType?: string | null
  memberPayBasis?: string | null
  retainerAmount?: number | null
  orgDefault?: string | null
}): ContractorPayBasis {
  if (!isContractorEmploymentType(employmentType)) return 'ALL_HOURS'
  const fallback: ContractorPayBasis =
    isPayBasis(orgDefault) && orgDefault !== 'RETAINER'
      ? orgDefault
      : DEFAULT_CONTRACTOR_PAY_BASIS
  if (!isPayBasis(memberPayBasis)) return fallback
  if (memberPayBasis === 'RETAINER' && !(retainerAmount && retainerAmount > 0))
    return fallback
  return memberPayBasis
}

/**
 * True when the entry is paid under this basis: always for ALL_HOURS and
 * RETAINER (covered by the retainer), billable entries only for
 * BILLABLE_HOURS.
 */
export function isPaidTimeEntry(
  entry: { billable?: boolean | null },
  basis: ContractorPayBasis,
): boolean {
  return basis !== 'BILLABLE_HOURS' || entry.billable === true
}

/**
 * Hourly pay an entry earns: its pay value when paid, 0 when unpaid, and null
 * for a retainer (pay is the period amount, not per entry) or unknown rates.
 */
export function paidAmountForTimeEntry(
  entry: { billable?: boolean | null },
  payValue: number | null | undefined,
  basis: ContractorPayBasis,
): number | null {
  if (basis === 'RETAINER' || payValue == null) return null
  return isPaidTimeEntry(entry, basis) ? payValue : 0
}

/** Short label for a basis, for settings and summaries. */
export const CONTRACTOR_PAY_BASIS_LABELS: Record<ContractorPayBasis, string> = {
  ALL_HOURS: 'All hours worked',
  BILLABLE_HOURS: 'Billable hours only',
  RETAINER: 'Retainer',
}
